import React, { useMemo, useEffect, useState } from 'react';
import { UserSwitch } from 'phosphor-react';
import { COLORS } from '../../../utils/colors';

import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { useParamMainBuyerId } from '../../../utils/hooks/useParamMainBuyerId';
import { AppUserApiResponse } from '@dill/dill-shared/dist/src';
import { getUserByBuyers } from '../../../redux/services/authService';
import { getJobs, updateBillingRepJobs } from '../../../redux/services/jobsService';
import { openDillToast } from '../../../utils/helpers';
import { DillModal, DillButton, DillSelect } from '../Dill';

export const AppCustomerSettingsModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { userBuyers } = useAppSelector((state) => state.auth);
  const [buyerRep, setBuyerRep] = useState<AppUserApiResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedMainBuyerId && open) {
      dispatch(getUserByBuyers({ buyerId: selectedMainBuyerId }));
    }
  }, [open]);

  const fetchUserBuyer = useMemo(() => {
    if (userBuyers.length > 0) {
      return userBuyers;
    }
    return [];
  }, [userBuyers]);

  const handleCloseModal = () => {
    setBuyerRep(null);
    setLoading(false);
    handleClose();
  };

  const handleUpdateJobBillingRep = async () => {
    setLoading(true);
    if (buyerRep?.userIds && selectedMainBuyerId) {
      const billingRepJobs = await dispatch(
        updateBillingRepJobs({ ...buyerRep.userIds, mainBuyerId: selectedMainBuyerId })
      );
      if (billingRepJobs.type === 'jobs/updateBillingRepJobs/fulfilled') {
        openDillToast({ type: 'SUCCESS', message: 'Successfully updated colleague' });
        await dispatch(getJobs({ mainBuyerId: selectedMainBuyerId }));
        handleCloseModal();
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to update colleague' });
        setLoading(false);
      }
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Lien Waiver Settings"
      subtitle="Manage billing representative assignments"
      maxWidth="480px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Update"
            onClick={handleUpdateJobBillingRep}
            width="100px"
            isLoading={loading}
            className="flex justify-center"
            isDisabled={buyerRep === null || loading}
          />
        </>
      }>
      <div
        className="flex items-start gap-3 p-3 rounded-lg"
        style={{ backgroundColor: COLORS.GREY_50 }}>
        <div style={{ color: COLORS.PRIMARY_500 }} className="mt-0.5">
          <UserSwitch size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium mb-1" style={{ color: COLORS.TEXT_SECONDARY }}>
            Billing Representative
          </p>
          <p className="text-sm mb-3" style={{ color: COLORS.TEXT_SECONDARY }}>
            Colleague out of office? Reassign the billing rep across all jobs.
          </p>
          <DillSelect
            value={buyerRep?.userIds?.userId ?? ''}
            options={fetchUserBuyer.map((userBuyer) => ({
              label: `${userBuyer?.firstName ?? ''} ${userBuyer?.lastName ?? ''}`,
              value: userBuyer.userIds?.userId ?? '',
            }))}
            onChange={(v) => {
              const uu = fetchUserBuyer.find((val) => val.id === v);
              if (uu) setBuyerRep(uu);
            }}
            placeholder="Select colleague"
          />
        </div>
      </div>
    </DillModal>
  );
};
